import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { IWeissmanModalComponent } from '../../Compliance/WeissmanModalService';
import { TopLevelCompanyData } from '../../Core-Repositories/actionView.repository';
import { MessageBoxService } from '../../UI-Lib/Message-Box/messagebox.service.upgrade';
import { ActionViewPersistenceService } from '../../Task/ActionView/Action.View.Persistence.Service.upgrade';
import { ProcessingService } from '../processing.service.upgrade';
import { RevisionChangeTypes } from 'src/app/constants.new';

export interface ProcessAppealWarrantedParams {
    taskIDs: number[];
    searchTimestamp: Date;
    runWithBuffer: boolean;
    updateAppealRecommendation: boolean;
    topLevelCompanyId?: number;
    topLevelCompanyIds?: number[];
    topLevelCompanyData?: TopLevelCompanyData[];
    hasMultipleCompanies?: boolean;
    litigationReferralEnabled?: boolean;
}

export interface ProcessAppealWarrantedForm {
    taskIDs: number[];
    appealLevelID: number;
    comment: string;
    isPinned: boolean;
    searchTimestamp: Date;
    updateAppealRecommendation: boolean;
    revisionChangeTypeID: number;
    newRevisionDescription: string;
}

interface AppealLevelsResponse {
    appealLevels: any[];
    isClientApprovalRequired: boolean;
    validationMessage: string;
    warningMessage: string;
    litigationReferralsEnabled?: boolean;
}

@Component({
    selector: 'process-appeal-warranted',
    templateUrl: './processAppealWarranted.component.html',
    standalone: false,
})
export class ProcessAppealWarrantedComponent
    implements OnInit, IWeissmanModalComponent<ProcessAppealWarrantedParams, Core.BulkOperationResult[]>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _processingService: ProcessingService,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _toastr: ToastrService,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
    ) {}

    params: ProcessAppealWarrantedParams;
    result: Core.BulkOperationResult[];

    loading: boolean;
    loadingMessage: string;
    validationMessage: string;
    appealLevels: any[];
    isClientApprovalRequired: boolean;
    isCompanyLitigationReferralsEnabled: boolean;
    isForlitigationReferral: boolean = true;
    litigationReferralChecked: boolean = true;

    payload: ProcessAppealWarrantedForm;
    revisionChangeTypes = RevisionChangeTypes;

    get cancelLabel(): string {
        return this.appealLevels ? 'Cancel' : 'Close';
    }

    get isSelectedAppealLevelLitigation(): boolean {
        const selectedAppealLevel = this.appealLevels?.find((x) => x.appealLevelID === this.payload?.appealLevelID);
        return !!selectedAppealLevel?.litigation;
    }

    get shouldShowLitigationReferralCheckbox(): boolean {
        return this.isSelectedAppealLevelLitigation && this.isCompanyLitigationReferralsEnabled;
    }

    get litigationReferralLabel(): string {
        return 'When company allows litigation referrals, add litigation referral task to appeal and check the RLS checkbox.';
    }

    get litigationReferralEnabled(): boolean {
        if (this.params?.litigationReferralEnabled !== undefined) {
            return this.params.litigationReferralEnabled;
        }
        return this.isSelectedAppealLevelLitigation && this.isCompanyLitigationReferralsEnabled;
    }

    get hasCommentText(): boolean {
        return !!this.payload?.comment?.trim();
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;

        const remembered = this.getRememberedRevisionChange();
        this.payload = {
            taskIDs: this.params.taskIDs,
            appealLevelID: null,
            comment: null,
            isPinned: false,
            searchTimestamp: this.params.searchTimestamp,
            updateAppealRecommendation: this.params.updateAppealRecommendation,
            revisionChangeTypeID: remembered.revisionChangeTypeID,
            newRevisionDescription: remembered.newRevisionDescription,
        };

        try {
            const result = (await this._processingService.getAppealLevels(this.params.taskIDs)) as AppealLevelsResponse;
            this.validationMessage = result.validationMessage;
            this.appealLevels = result.appealLevels;

            this.isClientApprovalRequired = result.isClientApprovalRequired;
            this.isCompanyLitigationReferralsEnabled = this.resolveLitigationReferralsEnabled(result);

            if (result.warningMessage) {
                await this._messageBoxService.warning(result.warningMessage);
            }

            if (this.appealLevels?.length) {
                this.payload.appealLevelID = this.appealLevels[0].appealLevelID;
                this.referralCheckboxes();
            }
        } finally {
            this.loading = false;
        }
    }

    onAppealLevelChange(): void {
        this.referralCheckboxes();
    }

    onLitigationReferralChange(): void {
        this.referralCheckboxes();
    }

    onCommentChange(): void {
        if (!this.hasCommentText) {
            this.payload.isPinned = false;
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    async process(): Promise<void> {
        this.loading = true;
        this._actionViewPersistenceService.appealWarrantedRevisionChangeTypeID = this.payload.revisionChangeTypeID;
        this._actionViewPersistenceService.appealWarrantedRevisionDescription = this.payload.newRevisionDescription;

        if (this.params.runWithBuffer) {
            try {
                // This is "experimental" at the moment; I'm leaving in the ability to set a processingBufferOverride in
                // a console in case we run into any obscure problems here and for instance need to set it to 1 to clear
                // things out. Once this has been in place for a while we can probably get rid of the override thing.
                await this._runModalResultWithBuffer();
            } finally {
                this.loading = false;
                this._bsModalRef.hide();
            }
        } else {
            try {
                this.result = await this._processingService.processAppealWarranted(
                    this.payload,
                    this.isForlitigationReferral,
                );
            } catch (err) {
                this._toastr.error('An unexpected error has occurred');
            } finally {
                this.loading = false;
                this._bsModalRef.hide();
            }
        }
    }

    private getRememberedRevisionChange(): { revisionChangeTypeID: number; newRevisionDescription: string } {
        const typeID = this._actionViewPersistenceService.appealWarrantedRevisionChangeTypeID;
        const description = this._actionViewPersistenceService.appealWarrantedRevisionDescription;

        if (typeID === RevisionChangeTypes.OTHER && description?.trim()) {
            return { revisionChangeTypeID: typeID, newRevisionDescription: description };
        }

        return { revisionChangeTypeID: RevisionChangeTypes.NO_CHANGE, newRevisionDescription: '' };
    }

    private referralCheckboxes(): void {
        this.isForlitigationReferral =
            this.isSelectedAppealLevelLitigation &&
            this.isCompanyLitigationReferralsEnabled &&
            this.litigationReferralChecked;
    }

    private resolveLitigationReferralsEnabled(result: AppealLevelsResponse): boolean {
        if (typeof result.litigationReferralsEnabled === 'boolean') {
            return result.litigationReferralsEnabled;
        }

        return (this.params?.topLevelCompanyData ?? []).some((x) => x.litigationReferralsEnabled);
    }

    private async _runModalResultWithBuffer(): Promise<void> {
        const bufferSize = (window as any).processingBufferOverride || 20;

        const chunks = [];
        for (let i = 0; i < this.params.taskIDs.length; i += bufferSize) {
            chunks.push(this.params.taskIDs.slice(i, i + bufferSize));
        }

        let index = 1;
        this.result = [];

        const processChunk = async () => {
            if (chunks.length) {
                const chunk = chunks.shift();
                if (bufferSize === 1) {
                    this.loadingMessage = `Processing Task ${index} of ${this.params.taskIDs.length}...`;
                } else {
                    this.loadingMessage = `Processing Tasks ${index} - ${index + chunk.length - 1} of ${this.params.taskIDs.length}...`;
                }

                try {
                    const payload = structuredClone(this.payload);
                    payload.taskIDs = chunk;
                    const tempResult = await this._processingService.processAppealWarranted(
                        payload,
                        this.isForlitigationReferral,
                    );
                    this.result = [...this.result, ...tempResult];
                    index += chunk.length;
                    await processChunk();
                } catch (err) {
                    this.loading = false;
                    this.loadingMessage = null;
                    this._toastr.error('An unexpected error has occurred');
                }
            } else {
                this.loading = false;
                this.loadingMessage = null;
                this._bsModalRef.hide();
            }
        };

        await processChunk();
    }
}
